import type { Workspace } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import { Badge, cn } from '@sunstone/ui';

interface ReleaseLifecycleBadgeProps {
  workspace: Workspace;
  className?: string;
}

/**
 * Renders the release lifecycle state of a workspace as a shadcn Badge:
 *   - Unknown            (muted) — no currentVersion at all, e.g. a
 *                                  Duplo-discovered tenant with no
 *                                  apps.tfvars.json backfilled yet.
 *   - Deprecated         (red)
 *   - Disabled           (slate) — current release has been disabled by an
 *                                  operator_admin.
 *   - Latest             (green)
 *   - Upgrade available  (amber)  — also shown for legacy workspaces with no
 *                                  current_release_id, since neither flag is set.
 * Priority order when multiple could apply: Unknown > Deprecated > Disabled >
 * Latest / Upgrade available.
 */
export function ReleaseLifecycleBadge({ workspace, className }: ReleaseLifecycleBadgeProps) {
  if (!workspace.currentVersion) {
    return (
      <Badge className={cn('font-sans bg-muted text-muted-foreground', className)}>Unknown</Badge>
    );
  }
  if (workspace.isDeprecated) {
    return (
      <Badge
        className={cn(
          'font-sans bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
          className,
        )}
      >
        Deprecated
      </Badge>
    );
  }
  if (workspace.isDisabled) {
    return (
      <Badge
        className={cn(
          'font-sans bg-slate-50 text-slate-700 dark:bg-slate-950 dark:text-slate-300',
          className,
        )}
      >
        Disabled
      </Badge>
    );
  }
  if (workspace.isLatest) {
    return (
      <Badge
        className={cn(
          'font-sans bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300',
          className,
        )}
      >
        Latest
      </Badge>
    );
  }
  return (
    <Badge
      className={cn(
        'font-sans bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
        className,
      )}
    >
      Upgrade available
    </Badge>
  );
}
